class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.cacheDom();
    this.bindEvents();
    this.clearPanels();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  cacheDom() {
    this.items = Array.from(this.querySelectorAll('[data-mega-menu-item]'));
    this.triggers = Array.from(this.querySelectorAll('[data-mega-menu-trigger][aria-controls]'));
    this.panels = new Map(
      Array.from(this.querySelectorAll('[data-mega-menu-panel]')).map(panel => [
        panel.dataset.megaMenuPanel,
        panel,
      ]),
    );
  }

  bindEvents() {
    this.onItemEnter = this.onItemEnter.bind(this);
    this.onItemLeave = this.onItemLeave.bind(this);
    this.onMenuLeave = this.onMenuLeave.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onTriggerPointerDown = this.onTriggerPointerDown.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);

    this.items.forEach((item) => {
      item.addEventListener('mouseenter', this.onItemEnter);
      item.addEventListener('mouseleave', this.onItemLeave);
      item.addEventListener('focusin', this.onItemEnter);
    });

    this.triggers.forEach((trigger) => {
      trigger.addEventListener('pointerdown', this.onTriggerPointerDown);
      trigger.addEventListener('click', this.onTriggerClick);
    });

    this.addEventListener('mouseleave', this.onMenuLeave);
    this.addEventListener('focusout', this.onFocusOut);
  }

  unbindEvents() {
    this.items.forEach((item) => {
      item.removeEventListener('mouseenter', this.onItemEnter);
      item.removeEventListener('mouseleave', this.onItemLeave);
      item.removeEventListener('focusin', this.onItemEnter);
    });

    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('pointerdown', this.onTriggerPointerDown);
      trigger.removeEventListener('click', this.onTriggerClick);
    });

    this.removeEventListener('mouseleave', this.onMenuLeave);
    this.removeEventListener('focusout', this.onFocusOut);
  }

  // Touch devices have no hover: remember a tap that starts on a trigger while its panel is still closed
  onTriggerPointerDown(event) {
    const trigger = event.currentTarget;
    const isClosed = trigger.getAttribute('aria-expanded') !== 'true';

    this.touchedTrigger = event.pointerType === 'touch' && isClosed ? trigger : null;
  }

  // The first tap on a trigger opens its panel instead of following the link, the second tap follows the link
  onTriggerClick(event) {
    const trigger = event.currentTarget;

    if (this.touchedTrigger !== trigger) {
      return;
    }

    this.touchedTrigger = null;
    event.preventDefault();

    const item = trigger.closest('[data-mega-menu-item]');
    this.showPanel(item.dataset.megaMenuTarget, item);
  }

  onItemEnter(event) {
    const item = event.currentTarget;
    const target = item.dataset.megaMenuTarget;

    if (!target) {
      this.clearPanels();
      this.setActiveItem(item, false);
      return;
    }

    this.showPanel(target, item);
  }

  onItemLeave(event) {
    const relatedTarget = event.relatedTarget;

    if (relatedTarget && this.contains(relatedTarget)) {
      return;
    }

    this.clearPanels();
  }

  onMenuLeave() {
    this.clearPanels();
  }

  onFocusOut(event) {
    if (event.relatedTarget && this.contains(event.relatedTarget)) {
      return;
    }

    this.clearPanels();
  }

  showPanel(panelId, item) {
    const panel = this.panels.get(panelId);

    this.clearPanels();

    if (!panel) {
      this.setActiveItem(item, false);
      return;
    }

    panel.classList.remove('hidden');
    panel.setAttribute('aria-hidden', 'false');

    this.setActiveItem(item, true);
    this.activePanelId = panelId;
  }

  clearPanels() {
    this.panels.forEach((panel) => {
      panel.classList.add('hidden');
      panel.setAttribute('aria-hidden', 'true');
    });

    this.items.forEach((item) => {
      item.removeAttribute('data-state');

      const trigger = item.querySelector('[data-mega-menu-trigger]');
      if (trigger?.hasAttribute('aria-expanded')) {
        trigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

  setActiveItem(item, shouldExpand) {
    if (!item) {
      return;
    }

    item.dataset.state = 'active';

    const trigger = item.querySelector('[data-mega-menu-trigger]');
    if (trigger?.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', shouldExpand ? 'true' : 'false');
    }
  }
}

customElements.define('mega-menu', MegaMenu);
